import { useCallback, type JSX } from 'react'
import { Panel } from 'primereact/panel'
import { useDialerStore, type PreviewContact } from '../../store/dialerStore.js'
import { DialerControls } from '../../modules/dialer/DialerControls.js'
import { PacingMonitor } from '../../modules/dialer/PacingMonitor.js'
import { api } from '../../api/client.js'

interface NextContactResponse {
  contactId: string
  phone: string
  firstName?: string
  lastName?: string
  customFields?: Record<string, unknown>
}

export default function AgentDialerView(): JSX.Element {
  const activeCampaign = useDialerStore((s) => s.activeCampaign)
  const previewContact = useDialerStore((s) => s.previewContact)
  const pacing = useDialerStore((s) => s.pacing)
  const setPreviewContact = useDialerStore((s) => s.setPreviewContact)

  const handleNextContact = useCallback(async () => {
    if (!activeCampaign) return
    try {
      const res = await api.get<NextContactResponse | null>(
        `/campaigns/${activeCampaign.id}/preview/next`,
      )
      if (res) {
        const contact: PreviewContact = {
          id: res.contactId,
          name: [res.firstName, res.lastName].filter(Boolean).join(' ') || res.contactId,
          phone: res.phone,
        }
        setPreviewContact(contact)
      } else {
        setPreviewContact(null)
      }
    } catch {
      // leave current contact unchanged
    }
  }, [activeCampaign, setPreviewContact])

  const handleDial = useCallback(async () => {
    if (!activeCampaign || !previewContact) return
    try {
      await api.post(`/campaigns/${activeCampaign.id}/preview/dial`, {
        contactId: previewContact.id,
        phone: previewContact.phone,
      })
    } catch {
      // call origination failure surfaced via call:incoming absence
    }
  }, [activeCampaign, previewContact])

  const handleSkip = useCallback(async () => {
    if (!activeCampaign || !previewContact) return
    const contactId = previewContact.id
    setPreviewContact(null)
    try {
      await api.post(`/campaigns/${activeCampaign.id}/preview/skip`, { contactId })
    } catch {
      // skip recorded locally regardless of API failure
    }
  }, [activeCampaign, previewContact, setPreviewContact])

  return (
    <div className="p-4 flex flex-col gap-4">
      <Panel header="Preview Dialer">
        <DialerControls
          contact={previewContact}
          campaignName={activeCampaign?.name ?? '—'}
          onNextContact={() => { void handleNextContact() }}
          onDial={() => { void handleDial() }}
          onSkip={() => { void handleSkip() }}
        />
      </Panel>
      <Panel header="Pacing">
        <PacingMonitor
          dialRate={pacing.dialRate}
          abandonRate={pacing.abandonRate}
          pacingFactor={pacing.pacingFactor}
        />
      </Panel>
    </div>
  )
}
